<template lang="pug">
    bottom-modal(
        :show-close-button="true"
        id="import-planogram"
        data-keyboard="false"
        width=""
        :loading="onLoading('importing planogram') || onLoading('upload planogram file')"
    )
        span.text-center(slot="header-text") {{ type ? 'Replace ' : 'Import ' }}Planogram
        div.vertical-align(slot="body")
                div.col-md-offset-3.col-md-6
                    div.row
                        div.col-xs-12
                            div(v-if="!hasUploadedContent")
                                import-form(
                                    ref="importPlanogram",
                                    :type="type"
                                    @success="onFileUploaded",
                                    v-if="uploadProgress == 'notstarted'"
                                )
                                div.p-t-lg(v-if="uploadProgress == 'processing'")
                                    h3.text-center Processing uploaded file
                                    h3.text-center.text-muted._400.p-t-sm {{ uploadingProgress }}%

                                    .row
                                        .col-xs-12.p-t-md
                                            .progress.progress-striped
                                                .progress-bar.primary(:style="{ width: `${uploadingProgress}%`}")

                            import-mapper(
                                v-if="uploadProgress == 'uploaded' && hasUploadedContent"
                                :uploaded-content="uploadedContent"
                                :import-key="importKey"
                                ref="import"
                                @cancel="cancel"
                            )

                            div(v-if="isProcessingData")
                                include import-planogram-processing.pug

        button.btn.btn-fw.primary(
            v-if="!hasUploadedContent",
            slot="footer",
            @click="submit()",
            :disabled="onLoading('upload planogram file')"
        )
            v-loading(loader="upload planogram file") Upload

        span(slot="footer", v-else)
            button.btn.btn-fw.white(
                @click="cancel()",
                :disabled="onLoading('importing planogram') || uploadProgress == 'validation_failed'"
            ) Back

            button.btn.btn-fw.primary.m-l-xs(
                @click="importPlanogram()",
                :disabled="onLoading('importing planogram') || uploadProgress == 'validation_failed' || !inputFilled"
            )
                v-loading(loader="importing planogram") Import
</template>

<script>
import importForm from './import-planogram-form.vue';
import importMapper from './import-planogram-mapper.vue';
import Auth from '@js/plugins/auth';
import ImportData, {
    UPLOAD_NOTSTARTED,
    UPLOAD_PROCESSING,
    UPLOAD_UPLOADED,
    UPLOAD_VALIDATING,
    UPLOAD_VALIDATION_FAILED,
    UPLOAD_STORING,
    UPLOAD_COMPLETED,
} from '@js/plugins/import-data';
import { Event } from '../../../event-bus';

export default {
    components: { importForm, importMapper },

    props: {
        type: {
            type: Number,
            required: true,
        },
    },

    data() {
        return {
            importData: ImportData,
            planogramData: {
                total_planogram: 0,
                total_rows: 0,
            },
        };
    },

    computed: {
        hasUploadedContent() {
            return this.importData.rows.length;
        },

        uploadProgress() {
            return this.importData.uploadProgress;
        },

        importKey() {
            return this.importData.importKey;
        },

        inputFilled() {
            return (
                this.$store.state.planogram.description !== '' &&
                this.$store.state.planogram.effectiveDate !== null
            );
        },

        uploadedContent() {
            return {
                rows: this.importData.rows,
                headings: this.importData.headings,
                total_planogram: this.planogramData.total_planogram,
                total_rows: this.planogramData.total_rows,
            };
        },

        uploadingProgress() {
            return this.importData.getUploadingPercentage();
        },

        isProcessingData() {
            return (
                this.uploadProgress == UPLOAD_VALIDATING ||
                this.uploadProgress == UPLOAD_VALIDATION_FAILED ||
                this.uploadProgress == UPLOAD_STORING
            );
        },
    },

    created() {
        this.importData.initEchoIfNotInitialize();
    },

    mounted() {
        this.registerModalEvent();

        Event.$on('validating-data', mapper => {
            this.importData.setUploadProgress(UPLOAD_VALIDATING);
            this.importData.setMapper(mapper);

            // Start polling timer for import validation/storage stages
            this.importData.startPolling();
        });

        this.importData.onFileUploaded(async () => {
            const content = await this.getUploadedContent();
            this.planogramData.total_planogram = content.total_planogram;
            this.planogramData.total_rows = content.total_rows;
            this.importData.setRowsAndHeadings(content);
            this.clearLoaderState();
        });

        this.importData.onItemPassValidation(() => {
            this.processStoringData();
        });

        this.importData.onItemFailValidation(() => {
            this.clearLoaderState();
        });

        this.importData.onImportCompleted(() => {
            this.swal();
            Event.$emit('import-completed');
            this.clearLoaderState();
            $('#import-planogram').modal('hide');
        });

        this.importData.onServerError(() => {
            this.swalError({ text: 'Internal error occured' });
            Event.$emit('import-error');
            this.clearLoaderState();
            $('#import-planogram').modal('hide');
        });

        this.importData.onPollingRequested(async () => {
            const importStatus = await this.getImportStatus();

            this.importData.syncImportStatus(importStatus);
        });

        window.onbeforeunload = e => {
            if (this.uploadProgress !== UPLOAD_NOTSTARTED) {
                e.returnValue = '';
            }
        };
    },

    methods: {
        submit() {
            this.$refs.importPlanogram.submit();
            this.clearInputPlanogramData();
        },

        onFileUploaded(data) {
            this.importData.setUploadProgress(UPLOAD_PROCESSING);
            this.importData.setImportKey(data.import_key);
            this.importData.setTotalRows(data.total_rows);

            // Start polling timer for import upload stage
            this.importData.startPolling();
        },

        importPlanogram() {
            this.$refs.import.submit();
        },

        clearLoaderState() {
            this.endLoading('upload planogram file');
            this.endLoading('importing planogram');
            this.importData.stopPolling();
        },

        clearInputPlanogramData() {
            this.$store.state.planogram.description = '';
            this.$store.state.planogram.effectiveDate = null;
        },

        cancel() {
            this.importData.reset();
            this.clearInputPlanogramData();
            this.clearLoaderState();
        },

        registerModalEvent() {
            $('#import-planogram').on('hidden.bs.modal', () => {
                this.cancel();
            });
        },

        async getUploadedContent() {
            const url = Laroute.route(
                'franchise.inventory.planogram.read_data',
                {
                    importKey: this.importData.importKey,
                },
            );
            const { data } = await axios.get(url);
            return data;
        },

        async processStoringData() {
            const url = Laroute.route(
                'franchise.inventory.planogram.store_data',
                {
                    importKey: this.importData.importKey,
                },
            );
            let formData = {
                mapper: this.importData.mapper,
                metadata: {
                    description: this.$store.state.planogram.description,
                    effective_at: this.$store.state.planogram.effectiveDate.format(
                        'YYYY-MM-DD HH:mm:ss',
                    ),
                },
            };

            const { data } = await axios.post(url, formData);
            return data;
        },

        async getImportStatus() {
            const url = Laroute.route(
                'franchise.inventory.planogram.import_status',
                {
                    importKey: this.importData.importKey,
                },
            );
            const { data } = await axios.get(url);

            return data;
        },
    },
};
</script>



// WEBPACK FOOTER //
// resources/assets/js/franchise_component/inventory/planogram/import-planogram.vue